import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";

import { Dialog, Transition } from "@headlessui/react";
import { toast } from "react-toastify";

import { useAuth } from "../contexts/AuthContext";
import apiService from "../services/api";

const SEVERITY_OPTIONS = [
  {
    value: "info",
    label: "Info",
    description: "General informational notice",
    icon: "fa-circle-info",
    accent: {
      dot: "bg-sky-500",
      selectedBorder: "border-sky-500",
      selectedBg: "bg-sky-50 dark:bg-sky-500/10",
      selectedText: "text-sky-700 dark:text-sky-300",
      selectedRing: "ring-sky-500/20",
    },
  },
  {
    value: "warning",
    label: "Warning",
    description: "Something may affect the user's session",
    icon: "fa-triangle-exclamation",
    accent: {
      dot: "bg-amber-500",
      selectedBorder: "border-amber-500",
      selectedBg: "bg-amber-50 dark:bg-amber-500/10",
      selectedText: "text-amber-700 dark:text-amber-300",
      selectedRing: "ring-amber-500/20",
    },
  },
  {
    value: "critical",
    label: "Critical",
    description: "Urgent — requires the user's attention",
    icon: "fa-circle-exclamation",
    accent: {
      dot: "bg-red-500",
      selectedBorder: "border-red-500",
      selectedBg: "bg-red-50 dark:bg-red-500/10",
      selectedText: "text-red-700 dark:text-red-300",
      selectedRing: "ring-red-500/20",
    },
  },
];

const TARGET_OPTIONS = [
  {
    value: "global",
    label: "Global",
    hint: "All active users across every provider",
    icon: "fa-globe",
  },
  {
    value: "provider",
    label: "Provider",
    hint: "Everyone on a specific provider domain",
    icon: "fa-server",
  },
  {
    value: "lunakey",
    label: "LunaKey™",
    hint: "One specific user via their LunaKey™",
    icon: "fa-key",
  },
];

const SENT_BY_OVERRIDE_MAX_LENGTH = 40;

/**
 * Typeahead combobox used by both the Provider and Lunakey pickers.
 * Admin cannot free-type a value — only results returned by the server
 * are selectable, which keeps the resulting publish payload valid by
 * construction.
 */
function ScopeTypeahead({
  fetchFn,
  renderResult,
  placeholder,
  value,
  onChange,
  emptyLabel,
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const rows = await fetchFn(query);

        setResults(Array.isArray(rows) ? rows : []);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);

    return () => clearTimeout(t);
  }, [query, fetchFn]);

  useEffect(() => {
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target))
        setOpen(false);
    };

    document.addEventListener("mousedown", onClick);

    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <div className="relative" ref={wrapperRef}>
      {value ? (
        <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl border border-primary-500 bg-primary-50 dark:bg-primary-500/10 ring-1 ring-primary-500/20">
          <div className="flex items-center gap-2 text-sm text-gray-900 dark:text-white min-w-0">
            <i className="fas fa-check-circle text-primary-500 flex-shrink-0" />
            <div className="truncate">{renderResult(value)}</div>
          </div>
          <button
            className="text-xs font-medium text-primary-600 dark:text-primary-300 hover:text-primary-700 dark:hover:text-primary-200 flex-shrink-0"
            type="button"
            onClick={() => {
              onChange(null);
              setQuery("");
              setOpen(true);
            }}
          >
            Change
          </button>
        </div>
      ) : (
        <>
          <div className="relative">
            <i className="fas fa-search absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
            <input
              className="w-full text-sm rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700/60 text-gray-900 dark:text-white pl-9 pr-3 py-2.5 focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 focus:outline-none transition-colors placeholder:text-gray-400 dark:placeholder:text-gray-500"
              placeholder={placeholder}
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
            />
          </div>
          {open && (
            <div className="absolute z-10 mt-1.5 w-full max-h-64 overflow-auto rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-xl">
              {loading && (
                <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
                  <i className="fas fa-circle-notch fa-spin" />
                  Searching…
                </div>
              )}
              {!loading && results.length === 0 && (
                <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                  {emptyLabel}
                </div>
              )}
              {!loading &&
                results.map((r, idx) => (
                  <button
                    className="w-full text-left px-4 py-2.5 text-sm text-gray-900 dark:text-white hover:bg-primary-50 dark:hover:bg-primary-500/10 transition-colors first:rounded-t-xl last:rounded-b-xl"
                    key={`${idx}-${r.domain || r.code}`}
                    type="button"
                    onClick={() => {
                      onChange(r);
                      setOpen(false);
                      setQuery("");
                    }}
                  >
                    {renderResult(r)}
                  </button>
                ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * "Send Message" modal for Lunova admins. Two-step commit: (1) draft in
 * the primary panel, (2) confirm in the overlay panel with target-aware
 * copy. No publish hits the API until the confirm step is accepted, so
 * the Back button on the confirm panel is a safe undo.
 */
export default function SendAppMessageModal({
  isOpen,
  lockedLunakey,
  onClose,
  onPublished,
}) {
  const { user } = useAuth();
  // Resellers cannot publish `global` — the API rejects it with 403. We
  // hide the option here so they can't start composing a message they
  // won't be allowed to send, and default the radio to `provider`.
  const isAdmin = user?.role === "admin";
  const allowedTargets = useMemo(
    () =>
      isAdmin ? ["global", "provider", "lunakey"] : ["provider", "lunakey"],
    [isAdmin]
  );
  const initialTargetKind = lockedLunakey
    ? "lunakey"
    : isAdmin
      ? "global"
      : "provider";

  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [sentByOverride, setSentByOverride] = useState("");
  const [severity, setSeverity] = useState("info");
  const [targetKind, setTargetKind] = useState(initialTargetKind);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [selectedLunakey, setSelectedLunakey] = useState(lockedLunakey || null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);

  // Reset form each time the modal opens. Mirrors NewTicketModal pattern.
  useEffect(() => {
    if (!isOpen) {
      setTitle("");
      setBody("");
      setSentByOverride("");
      setSeverity("info");
      setTargetKind(initialTargetKind);
      setSelectedProvider(null);
      setSelectedLunakey(lockedLunakey || null);
      setConfirmOpen(false);
      setPublishing(false);
    }
  }, [isOpen, initialTargetKind, lockedLunakey]);

  // When opened in single-user mode, keep the locked target in sync if the
  // caller swaps users (e.g. a different device row).
  useEffect(() => {
    if (lockedLunakey) {
      setTargetKind("lunakey");
      setSelectedLunakey(lockedLunakey);
    }
  }, [lockedLunakey]);

  const fetchDomains = useMemo(
    () => async (q) => {
      const rows = await apiService.searchAppMessageDomains(q);

      return Array.isArray(rows) ? rows : [];
    },
    []
  );

  const fetchLunakeys = useMemo(
    () => async (q) => {
      const rows = await apiService.searchAppMessageLunakeys(q);

      return Array.isArray(rows) ? rows : [];
    },
    []
  );

  const formError = (() => {
    if (!title.trim()) return "Title is required";
    if (title.length > 200) return "Title must be 200 characters or fewer";
    if (sentByOverride.trim().length > SENT_BY_OVERRIDE_MAX_LENGTH) {
      return `Sender name must be ${SENT_BY_OVERRIDE_MAX_LENGTH} characters or fewer`;
    }
    if (!body.trim()) return "Body is required";
    if (body.length > 2000) return "Body must be 2000 characters or fewer";
    if (targetKind === "provider" && !selectedProvider)
      return "Pick a provider domain";
    if (targetKind === "lunakey" && !selectedLunakey) return "Pick a LunaKey™";

    return null;
  })();

  const confirmCopy = (() => {
    if (targetKind === "global") {
      return {
        title: "Send to all active users?",
        body:
          "This message will be sent to ALL active users across every provider. " +
          "Are you sure you want to send?",
      };
    }
    if (targetKind === "provider") {
      return {
        title: `Send to every user on ${selectedProvider?.domain}?`,
        body: `This message will be sent to ALL users on ${selectedProvider?.domain} (${selectedProvider?.userCount ?? 0} users). Are you sure you want to send?`,
      };
    }

    return {
      title: `Send to LunaKey™ ${selectedLunakey?.code}?`,
      body: `This message will be sent to the user tied to LunaKey™ ${selectedLunakey?.code} (${selectedLunakey?.claimedBy || "unknown email"} · ${selectedLunakey?.providerDomain || "unknown domain"}). Are you sure you want to send?`,
    };
  })();

  const buildPublishPayload = () => {
    const sender = sentByOverride.trim();
    const base = {
      title: title.trim(),
      body: body.trim(),
      severity,
      ...(sender ? { sentByOverride: sender } : {}),
    };

    if (targetKind === "global") {
      return { ...base, targetType: "global" };
    }
    if (targetKind === "provider") {
      return {
        ...base,
        targetType: "provider_domain",
        providerDomain: selectedProvider.domain,
      };
    }

    return {
      ...base,
      targetType: "direct_playlist",
      targetUserId: selectedLunakey.userId,
    };
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      await apiService.publishAppMessage(buildPublishPayload());
      onPublished?.();
    } catch (err) {
      toast.error(err?.message || "Failed to publish message");
    } finally {
      setPublishing(false);
      setConfirmOpen(false);
    }
  };

  const closeIfIdle = () => {
    if (publishing) return;
    onClose();
  };

  return (
    <Transition appear as={Fragment} show={isOpen}>
      <Dialog as="div" className="relative z-[70]" onClose={closeIfIdle}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50 backdrop-blur-md" />
        </Transition.Child>
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="relative w-full max-w-2xl transform overflow-visible rounded-2xl bg-white dark:bg-gray-800 shadow-2xl ring-1 ring-gray-200 dark:ring-white/5 transition-all">
                {/* Close button */}
                <button
                  aria-label="Close"
                  className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors"
                  type="button"
                  onClick={closeIfIdle}
                >
                  <i className="fas fa-times" />
                </button>

                {/* Header */}
                <div className="px-7 pt-7 pb-5 border-b border-gray-100 dark:border-gray-700/60">
                  <div className="flex items-start gap-3">
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center flex-shrink-0 shadow-lg shadow-primary-500/30">
                      <i className="fas fa-paper-plane text-white text-base" />
                    </div>
                    <div>
                      <Dialog.Title className="text-xl font-semibold text-gray-900 dark:text-white tracking-tight">
                        {lockedLunakey ? "Send Message" : "Send App Message"}
                      </Dialog.Title>
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        {lockedLunakey
                          ? "This message will be delivered to the user tied to this playlist."
                          : "Broadcast to every user, a single provider, or one specific LunaKey™."}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Body */}
                <div className="px-7 py-6 space-y-5">
                  {lockedLunakey ? (
                    /* Single-user scope chip — replaces the target picker. */
                    <div>
                      <label className="block text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-[0.08em] mb-2.5">
                        Recipient
                      </label>
                      <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-primary-500 bg-primary-50 dark:bg-primary-500/10 ring-1 ring-primary-500/20">
                        <div className="w-9 h-9 rounded-lg bg-primary-500/15 flex items-center justify-center flex-shrink-0">
                          <i className="fas fa-key text-primary-500 dark:text-primary-300 text-sm" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="font-mono text-sm text-gray-900 dark:text-white truncate">
                            {lockedLunakey.code}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {lockedLunakey.providerDomain || "—"}
                            {lockedLunakey.claimedBy
                              ? ` · ${lockedLunakey.claimedBy}`
                              : ""}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* Target */
                    <div>
                      <label className="block text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-[0.08em] mb-2.5">
                        Target
                      </label>
                      <div
                        className={`grid gap-2.5 ${isAdmin ? "grid-cols-3" : "grid-cols-2"}`}
                      >
                        {TARGET_OPTIONS.filter((o) =>
                          allowedTargets.includes(o.value)
                        ).map((o) => {
                          const active = targetKind === o.value;

                          return (
                            <button
                              aria-pressed={active}
                              className={`text-left px-4 py-3 rounded-xl border transition-all ${
                                active
                                  ? "border-primary-500 bg-primary-50 dark:bg-primary-500/10 ring-2 ring-primary-500/20"
                                  : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/40"
                              }`}
                              key={o.value}
                              type="button"
                              onClick={() => setTargetKind(o.value)}
                            >
                              <div className="flex items-center gap-2 mb-1">
                                <i
                                  className={`fas ${o.icon} text-sm ${
                                    active
                                      ? "text-primary-600 dark:text-primary-300"
                                      : "text-gray-400 dark:text-gray-500"
                                  }`}
                                />
                                <span
                                  className={`text-sm font-semibold ${
                                    active
                                      ? "text-primary-700 dark:text-primary-300"
                                      : "text-gray-900 dark:text-white"
                                  }`}
                                >
                                  {o.label}
                                </span>
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400 leading-snug">
                                {o.hint}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {!lockedLunakey && targetKind === "provider" && (
                    <div>
                      <label className="block text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-[0.08em] mb-2.5">
                        Provider Domain
                      </label>
                      <ScopeTypeahead
                        emptyLabel="No providers match"
                        fetchFn={fetchDomains}
                        placeholder="Start typing a provider domain…"
                        renderResult={(r) => (
                          <>
                            <span className="font-medium">{r.domain}</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                              {r.userCount} user{r.userCount === 1 ? "" : "s"}
                            </span>
                          </>
                        )}
                        value={selectedProvider}
                        onChange={setSelectedProvider}
                      />
                    </div>
                  )}

                  {!lockedLunakey && targetKind === "lunakey" && (
                    <div>
                      <label className="block text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-[0.08em] mb-2.5">
                        LunaKey™
                      </label>
                      <ScopeTypeahead
                        emptyLabel="No active LunaKeys™ match"
                        fetchFn={fetchLunakeys}
                        placeholder="Start typing a LunaKey™ code…"
                        renderResult={(r) => (
                          <>
                            <span className="font-mono text-sm">{r.code}</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                              {r.providerDomain || "—"}
                              {r.claimedBy ? ` · ${r.claimedBy}` : ""}
                            </span>
                          </>
                        )}
                        value={selectedLunakey}
                        onChange={setSelectedLunakey}
                      />
                    </div>
                  )}

                  {/* Severity */}
                  <div>
                    <label className="block text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-[0.08em] mb-2.5">
                      Severity
                    </label>
                    <div className="grid grid-cols-3 gap-2.5">
                      {SEVERITY_OPTIONS.map((o) => {
                        const active = severity === o.value;

                        return (
                          <button
                            aria-pressed={active}
                            className={`px-4 py-3 rounded-xl border text-left transition-all ${
                              active
                                ? `${o.accent.selectedBorder} ${o.accent.selectedBg} ring-2 ${o.accent.selectedRing}`
                                : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/40"
                            }`}
                            key={o.value}
                            type="button"
                            onClick={() => setSeverity(o.value)}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <span
                                className={`w-2 h-2 rounded-full ${o.accent.dot}`}
                              />
                              <span
                                className={`text-sm font-semibold capitalize ${
                                  active
                                    ? o.accent.selectedText
                                    : "text-gray-900 dark:text-white"
                                }`}
                              >
                                {o.label}
                              </span>
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 leading-snug">
                              {o.description}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Title */}
                  <div>
                    <div className="flex items-baseline justify-between mb-2.5">
                      <label className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-[0.08em]">
                        Title
                      </label>
                      <span className="font-mono text-[11px] text-gray-400 dark:text-gray-500 tabular-nums">
                        {title.length}/200
                      </span>
                    </div>
                    <input
                      className="w-full text-sm rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700/60 text-gray-900 dark:text-white px-4 py-3 focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 focus:outline-none transition-colors placeholder:text-gray-400 dark:placeholder:text-gray-500"
                      maxLength={200}
                      placeholder="e.g. Scheduled maintenance"
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                    />
                  </div>

                  {/* Sender */}
                  <div>
                    <div className="flex items-baseline justify-between mb-2.5">
                      <label className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-[0.08em]">
                        Sender Name
                      </label>
                      <span className="font-mono text-[11px] text-gray-400 dark:text-gray-500 tabular-nums">
                        {`${sentByOverride.trim().length}/${SENT_BY_OVERRIDE_MAX_LENGTH}`}
                      </span>
                    </div>
                    <input
                      className="w-full text-sm rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700/60 text-gray-900 dark:text-white px-4 py-3 focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 focus:outline-none transition-colors placeholder:text-gray-400 dark:placeholder:text-gray-500"
                      maxLength={SENT_BY_OVERRIDE_MAX_LENGTH}
                      placeholder="e.g. Service Provider"
                      type="text"
                      value={sentByOverride}
                      onChange={(e) => setSentByOverride(e.target.value)}
                    />
                  </div>

                  {/* Body */}
                  <div>
                    <div className="flex items-baseline justify-between mb-2.5">
                      <label className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-[0.08em]">
                        Message
                      </label>
                      <span className="font-mono text-[11px] text-gray-400 dark:text-gray-500 tabular-nums">
                        {body.length}/2000
                      </span>
                    </div>
                    <textarea
                      className="w-full text-sm rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700/60 text-gray-900 dark:text-white px-4 py-3 focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 focus:outline-none transition-colors resize-none placeholder:text-gray-400 dark:placeholder:text-gray-500"
                      maxLength={2000}
                      placeholder="Playback may be interrupted tonight between 9 and 11 PM ET…"
                      rows={4}
                      value={body}
                      onChange={(e) => setBody(e.target.value)}
                    />
                  </div>
                </div>

                {/* Footer */}
                <div className="px-7 py-5 border-t border-gray-100 dark:border-gray-700/60 bg-gray-50/50 dark:bg-gray-900/30 rounded-b-2xl flex items-center justify-between gap-3">
                  <div className="text-xs text-red-500 flex-1 min-w-0">
                    {formError && (
                      <span className="inline-flex items-center gap-1.5">
                        <i className="fas fa-circle-exclamation" />
                        {formError}
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button
                      className="px-5 py-2.5 text-sm font-medium rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                      type="button"
                      onClick={closeIfIdle}
                    >
                      Cancel
                    </button>
                    <button
                      className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-xl bg-gradient-to-r from-primary-500 to-primary-600 text-white hover:shadow-lg hover:shadow-primary-500/30 hover:-translate-y-0.5 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none disabled:translate-y-0 transition-all"
                      disabled={!!formError}
                      type="button"
                      onClick={() => setConfirmOpen(true)}
                    >
                      <i className="fas fa-paper-plane text-xs" />
                      Publish
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>

        {/* Confirm dialog — nested so a click on the backdrop dismisses it
            but leaves the composition panel intact. */}
        <Transition appear as={Fragment} show={confirmOpen}>
          <Dialog
            as="div"
            className="relative z-[80]"
            onClose={() => !publishing && setConfirmOpen(false)}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black/55 backdrop-blur-md" />
            </Transition.Child>
            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-200"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-150"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 shadow-2xl ring-1 ring-gray-200 dark:ring-white/5 transition-all">
                    <div className="px-7 pt-7 pb-5">
                      <div className="w-12 h-12 rounded-2xl bg-amber-50 dark:bg-amber-500/10 flex items-center justify-center mb-4 ring-1 ring-amber-500/20">
                        <i className="fas fa-triangle-exclamation text-amber-500 text-lg" />
                      </div>
                      <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-white tracking-tight">
                        {confirmCopy.title}
                      </Dialog.Title>
                      <p className="mt-2 text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                        {confirmCopy.body}
                      </p>
                    </div>

                    <div className="px-7 py-4 border-t border-gray-100 dark:border-gray-700/60 bg-gray-50/50 dark:bg-gray-900/30 rounded-b-2xl flex justify-end gap-2">
                      <button
                        className="px-5 py-2.5 text-sm font-medium rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 transition-colors"
                        disabled={publishing}
                        type="button"
                        onClick={() => setConfirmOpen(false)}
                      >
                        Back
                      </button>
                      <button
                        className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-xl bg-gradient-to-r from-primary-500 to-primary-600 text-white hover:shadow-lg hover:shadow-primary-500/30 hover:-translate-y-0.5 disabled:opacity-50 disabled:shadow-none disabled:translate-y-0 transition-all"
                        disabled={publishing}
                        type="button"
                        onClick={handlePublish}
                      >
                        {publishing ? (
                          <>
                            <i className="fas fa-circle-notch fa-spin text-xs" />
                            Publishing…
                          </>
                        ) : (
                          <>
                            <i className="fas fa-paper-plane text-xs" />
                            Yes, send
                          </>
                        )}
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>
      </Dialog>
    </Transition>
  );
}
